import React, { FC, ReactNode } from 'react';
import LoadingOverlay from '../../shared/components/LoadingOverlay';
import { LoadingWrapper } from '../../shared/components/LoadingWrapper';
import { BannerSlider } from '../../shared/slider/BannerSlider';
import { BannerItem } from '../../shared/slider/BannerItem';
import { useSelector } from 'react-redux';
import { AppState } from '../../store/appState';
import { ContainerInner } from '../../shared/common/ContainerInner';
import { BlankCoverProvider } from '../../contexts/blank-cover';
import './EntryPage.scss';
import { cn } from '@bem-react/classname';
import { parseLoanFormat } from '../../../models/LoanFormat';
import { CatalogueProductGroup, LibxProductGroup, ProductGroup } from '../../shared/product/ProductGroup';
import { EntryPageSidebar } from './EntryPageSidebar';
import { useEntryPageQuery, UseEntryPageQueryData } from './useEntryPageQuery';
import { BannerCategory, EntryPageItemOut } from '../../store/api';
import { EntryPageType } from '../../../models/EntryPage';
import { log } from '../../shared/utils/log';
import { useParams } from 'react-router';
import { LOANFORMAT_ALL_OBJECT, loanFormatKeyToObject } from '../../store/loanFormat/actions';

const block = cn('new-entry-page');

type Props = {
    pageType: EntryPageType;
};

export const EntryPage: FC<Props> = ({ pageType }) => {
    const { loanFormat: loanFormatParam } = useParams<{ loanFormat: string }>();
    const loanFormat = parseLoanFormat(loanFormatParam);
    const loanFormatUrl = loanFormatKeyToObject(loanFormatParam)?.urlKey ?? LOANFORMAT_ALL_OBJECT.urlKey;
    const ageCategory = useSelector((state: AppState) => state.ageCategory);
    const entryPage = useEntryPageQuery(pageType, { loanFormat, ageCategory });
    const isLoading = entryPage.status === 'loading';

    return (
        <EntryPageContainer
            pageType={pageType}
            loanFormatUrl={loanFormatUrl}
            entryPageData={entryPage.data}
            isLoading={isLoading}
        >
            {isLoading ? (
                <LoadingWrapper>
                    <LoadingOverlay loading={true} />
                </LoadingWrapper>
            ) : (
                <BlankCoverProvider value={false}>
                    <EntryPageProductGroups productGroups={entryPage.data.productGroups} />
                </BlankCoverProvider>
            )}
        </EntryPageContainer>
    );
};

type EntryPageContainerProps = {
    pageType: EntryPageType;
    entryPageData: UseEntryPageQueryData;
    loanFormatUrl: string;
    isLoading: boolean;
    readonly children: ReactNode;
};

export const EntryPageContainer: FC<EntryPageContainerProps> = ({
    pageType,
    entryPageData,
    loanFormatUrl,
    children,
    isLoading
}) => {
    const orderLists = useSelector((state: AppState) => state.orderlists.list);

    const topBanner = entryPageData.topBanner;

    const hasSidebar = entryPageData.sidebar.available;

    return (
        <div className={block()}>
            {topBanner && topBanner.banners && (
                <BannerSlider {...getBannerProps(topBanner.bannerCategory)}>
                    {topBanner.banners.map((bannerItem, i) => (
                        <BannerItem key={i} banner={bannerItem} />
                    ))}
                </BannerSlider>
            )}
            <ContainerInner className="entry-page" isLoading={isLoading}>
                {hasSidebar && (
                    <div className={block('sidebar')}>
                        <EntryPageSidebar
                            key="publisher-page-sidebar"
                            pageType={pageType}
                            loanFormatUrl={loanFormatUrl}
                            topEaudiobooks={entryPageData.sidebar.topEaudiobooks}
                            topEbooks={entryPageData.sidebar.topEbooks}
                            topEpress={entryPageData.sidebar.topEpress}
                            quicklinks={entryPageData.sidebar.quicklinks}
                            orderLists={orderLists}
                            titleOfMonth={entryPageData.sidebar.titleOfMonth}
                            languages={entryPageData.sidebar.languages}
                        />
                    </div>
                )}
                <div className={block('inner-content', { 'with-sidebar': hasSidebar })}>{children}</div>
            </ContainerInner>
        </div>
    );
};

type EntryPageProductGroupsProps = {
    readonly productGroups: ReadonlyArray<EntryPageItemOut>;
};

export const EntryPageProductGroups: FC<EntryPageProductGroupsProps> = ({ productGroups }) => {
    if (productGroups.length === 0) {
        return null;
    }

    return (
        <>
            {productGroups.map((productGroup, i) => {
                switch (productGroup.type) {
                    case 'banner':
                        return (
                            <BannerSlider key={i} {...getBannerProps(productGroup.bannerCategory)}>
                                {productGroup.banners &&
                                    productGroup.banners.map((bannerItem, i) => (
                                        <BannerItem key={i} banner={bannerItem} />
                                    ))}
                            </BannerSlider>
                        );
                    case 'lc-product-group':
                        return (
                            <LibxProductGroup key={i} title={productGroup.title!} productGroupId={productGroup.id!} />
                        );
                    case 'product-group':
                        if (!productGroup.productSpec || !productGroup.productSpec.filters) {
                            return (
                                <ProductGroup key={i} title={productGroup.title!} productGroupId={productGroup.id!} />
                            );
                        }

                        return (
                            <CatalogueProductGroup
                                key={i}
                                title={productGroup.title!}
                                seeAllUrl={productGroup.seeAllUrl!}
                                productSpec={productGroup.productSpec}
                            />
                        );
                    default:
                        log.error(`Invalid product group type ${productGroup.type}`, productGroup);
                        return null;
                }
            })}
        </>
    );
};

const getBannerProps = (bannerCategory?: BannerCategory) => {
    if (bannerCategory === 'ENTRY_PAGE_SUB_BANNER') {
        return {
            offset: 250,
            itemWidth: 380,
            itemHeight: 230,
            centerMode: false,
            showButtons: true
        };
    } else if (bannerCategory === 'CATEGORY_PAGE_TOP_BANNER') {
        return {
            offset: 220,
            itemWidth: 600,
            itemHeight: 200,
            showDots: false,
            showShadows: false,
            minimumItemsToShow: 3,
            buttonPosition: 'bottom'
        };
    }

    return {
        offset: 320,
        itemWidth: 2000,
        itemHeight: 300,
        showButtons: false
    };
};
